export const TERMS_OF_SERVICE_AND_PRIVACY_NOTICE = `PEAK4 Mobile Application
Terms of Service and Privacy Notice

PART A - TERMS OF SERVICE
1. Introduction and Acceptance of Terms
These Terms of Service ("Terms") govern access to and use of the PEAK4 Mobile Application and associated digital services (the "App"), operated by PEAK4 ("PEAK4", "we", "us", "our").
By downloading, accessing, or using the App, users confirm that they have read, understood, and agree to be legally bound by these Terms.
If a user does not agree to these Terms, they must not download, access, or use the App.

2. Nature and Purpose of the App
The App is a mobile digital platform designed to facilitate access to wellbeing-related services, resources, and integrated third-party systems.
Depending on programme participation and organisational arrangements, the App may provide access to:
- Wearable wellbeing programmes and associated integrations
- Educational materials, modules, and learning systems
- Occupational health-related services or portals
- Third-party digital platforms and service providers
- The PEAK4 online store or related commercial services
The App operates primarily as a platform interface and integration mechanism. Certain services may be delivered directly by PEAK4, while others are provided by independent third parties.

3. Access Model and Sponsoring Organisations
Access to the App is typically provided pursuant to a contractual arrangement between PEAK4 and a sponsoring organisation, such as an employer or contracting entity ("Sponsoring Organisation").
Users acknowledge and agree that:
- They do not enter into a direct payment contract with PEAK4 for access to the App (unless expressly stated otherwise)
- Access rights derive from arrangements with the Sponsoring Organisation
- Access may be modified, restricted, or withdrawn in accordance with those arrangements
PEAK4 reserves the right to suspend or terminate access where required under its contractual, legal, or operational obligations.

4. User Accounts and Security
User accounts may be provisioned by PEAK4 or by an authorised Sponsoring Organisation.
Users are responsible for:
- Maintaining the confidentiality of login credentials
- Ensuring credentials are not shared or misused
- Promptly notifying PEAK4 of any suspected unauthorised access
PEAK4 may suspend or restrict access where necessary for security, compliance, or operational reasons.

5. Third-Party Platforms and Services
The App may contain links to, or integrations with, third-party systems and service providers, including wearable technology providers, learning management systems, occupational health platforms, analytics services, and e-commerce platforms.
Users acknowledge that:
- Third-party services operate independently of PEAK4
- Such services are governed by their own terms and privacy policies
- PEAK4 does not control third-party systems, infrastructure, or processing activities
To the maximum extent permitted by law, PEAK4 accepts no responsibility for the operation, availability, security, accuracy, or data handling practices of third-party providers.

6. Acceptable Use
Users agree to use the App only for lawful, authorised, and legitimate purposes.
Prohibited conduct includes:
- Interfering with the App's security, integrity, or functionality
- Misusing authentication credentials
- Attempting unauthorised access to systems or data
- Extracting, copying, or redistributing content without permission
- Engaging in unlawful, abusive, or fraudulent activity

7. Health and Wellbeing Disclaimer
Information, metrics, insights, and materials accessible via the App are provided for general informational and wellbeing purposes only.
They do not constitute medical advice, clinical diagnosis, or treatment recommendations.
Users should seek appropriate qualified healthcare advice where required.

8. Intellectual Property
All intellectual property rights in the App, including software, branding, content, and materials, are owned by or licensed to PEAK4.
Users are granted a limited, non-transferable, non-exclusive licence to use the App for authorised purposes only.

9. Limitation of Liability
To the maximum extent permitted by law, PEAK4 shall not be liable for:
- Indirect, incidental, or consequential losses
- Loss of data, revenue, or business opportunity
- Service interruptions or technical failures
- Third-party system or device failures
- Reliance on informational content
Nothing in these Terms excludes liability that cannot lawfully be excluded under applicable law.

10. Suspension and Termination
PEAK4 may suspend or terminate access where:
- These Terms are breached
- Security, legal, or compliance risks arise
- Sponsoring arrangements change or conclude

11. Amendments
PEAK4 may amend these Terms from time to time. Updated Terms will be made available within the App. Continued use of the App constitutes acceptance of revised Terms.

12. Governing Law
These Terms are governed by the laws of England and Wales. The courts of England and Wales shall have exclusive jurisdiction.

PART B - PRIVACY NOTICE
13. Regulatory Framework
PEAK4 processes personal data in accordance with:
- UK General Data Protection Regulation (UK GDPR)
- Data Protection Act 2018
- Applicable UK data protection legislation
Depending on the service model, PEAK4 may act as:
- A data controller,
- A joint controller, or
- A data processor on behalf of a Sponsoring Organisation.

14. Categories of Personal Data
Subject to programme participation and App usage, PEAK4 may process:
- Identity and contact information
- Account and authentication data
- Technical, device, and usage data
- Wellbeing programme participation data
- Wearable-derived physiological or activity data (where applicable)
- Communications and support interactions
Certain information may constitute special category data under UK GDPR.

15. Lawful Bases for Processing
Processing is undertaken only where a lawful basis exists, including:
- Contractual necessity
- Legitimate interests (subject to balancing assessment)
- Legal or regulatory obligations
- Explicit consent (where required)

16. Special Category Data
Where special category data (including health-related or biometric information) is processed, PEAK4 relies on an appropriate Article 9 UK GDPR condition, which may include:
- Explicit consent
- Employment, occupational health, or social protection conditions (where applicable)

17. Purposes of Processing
Personal data is processed for defined purposes including:
- User authentication and platform administration
- Delivery of wellbeing programmes
- Facilitation of occupational health-related services
- System security and fraud prevention
- Compliance with contractual and legal obligations

18. Data Sources
Personal data may be obtained from:
- Users directly
- Sponsoring Organisations
- Integrated third-party providers
- Wearable technology platforms

19. Third-Party Processing
Some services accessible via the App are provided through independent third-party platforms.
Such providers may act as independent data controllers for their own processing activities. Users should consult their respective privacy notices.

20. Data Disclosure
Personal data is disclosed only where necessary for:
- Service delivery
- Contractual performance
- Legal compliance
PEAK4 does not sell personal data.

21. International Transfers
Where personal data is transferred outside the United Kingdom, appropriate safeguards are implemented in accordance with UK GDPR requirements.

22. Data Security
PEAK4 implements appropriate technical and organisational measures to protect personal data against unauthorised access, loss, or misuse.

23. Data Retention
Personal data is retained only for as long as necessary to fulfil operational, contractual, or legal purposes, and in accordance with retention policies.

24. Data Subject Rights
Individuals have rights under UK GDPR, including:
- Right of access
- Right to rectification
- Right to erasure
- Right to restriction
- Right to data portability (where applicable)
- Right to object
Requests may be submitted using the contact details below.

25. Contact and Complaints
PEAK4 Ltd, Avon View Offices 90 High Street, Bidford-On-Avon, Alcester, England, B50 4AF
helpdesk@peak4.co.uk
Individuals have the right to lodge a complaint with the Information Commissioner's Office (ICO).

PART C - WEARABLE AND BIOMETRIC DATA PROVISIONS
26. Wearable Programme Participation
Where users participate in wearable-based programmes:
- Data is generated via third-party wearable devices and platforms
- PEAK4 does not control device hardware, sensors, firmware, or operating systems
- Data accuracy and availability depend upon third-party technologies

27. Nature of Wearable Data
Wearable-derived metrics:
- Are used solely for wellbeing and programme-related purposes
- Do not constitute medical diagnosis or clinical monitoring
- Should not be relied upon for medical decision-making

28. Special Category Classification
Certain wearable-derived metrics may constitute special category data. Processing occurs only where an appropriate lawful condition under UK GDPR applies.

29. Third-Party Responsibilities
Wearable device and platform providers operate independently and are responsible for their own infrastructure, data processing activities, and compliance obligations.
PEAK4 accepts no responsibility for device performance or data accuracy.

30. Voluntary Participation
Participation in wearable programmes is voluntary unless otherwise required by the policies of the Sponsoring Organisation.`;
